import * as am4charts from '@amcharts/amcharts4/charts';
import * as am4core from '@amcharts/amcharts4/core';
import { color } from '@amcharts/amcharts4/core';
import {
  setupBaseChart,
  setupLegendBase,
  setupMarkerColumnBase,
  setupSeriesTooltipBase,
  setupValueAxisBase,
} from 'features/shared/ui/charts';

import { numberWithSpaces } from 'utils/numberWithSpaces';
import { LineSeries, XYChart } from '@amcharts/amcharts4/charts';

export interface ChartData {
  value: number;
  date: string;
}

export interface Series {
  color: string;
  title: string;
  values: ChartData[];
  total?: ChartData[];
}

const setupLegend = () => {
  const legend = setupLegendBase(false);

  legend.paddingLeft = -25;
  legend.position = 'bottom';
  legend.dy = 14;
  legend.itemContainers.template.paddingLeft = 20;
  legend.itemContainers.template.paddingRight = 24;

  const marker = legend.markers.template;

  setupMarkerColumnBase(marker, true);

  return legend;
};

function setupCategoryAxis(categoryAxis: am4charts.CategoryAxis) {
  categoryAxis.dataFields.category = 'date';
  categoryAxis.renderer.minGridDistance = 30;

  categoryAxis.renderer.axisFills.template.disabled = false;
  categoryAxis.renderer.axisFills.template.fillOpacity = 1;
  categoryAxis.renderer.axisFills.template.fill = am4core.color('#f4f5f6');
  categoryAxis.renderer.grid.template.disabled = true;

  categoryAxis.fillRule = function (dataItem, index) {
    dataItem.axisFill.visible = !!(((index || 0) + 1) % 2);
  };

  categoryAxis.renderer.labels.template.fill = color('rgba(9, 21, 38, 0.6)');
}

function setupValueAxis(valueAxis: am4charts.ValueAxis) {
  setupValueAxisBase(valueAxis);

  valueAxis.renderer.grid.template.above = true;
  valueAxis.renderer.axis.strokeWidth = 2;
  valueAxis.renderer.baseGrid.disabled = true;
}

function setupSeries(series: LineSeries, name: string, data: ChartData[], isTotal: boolean) {
  setupSeriesTooltipBase(series);

  series.dataFields.valueY = 'value';
  series.dataFields.categoryX = 'date';
  series.name = name;
  series.data = data;

  if (isTotal) {
    series.hiddenInLegend = true;
    series.strokeOpacity = 0.1;
    series.fillOpacity = 0.1;
  } else {
    series.strokeWidth = 3;

    const bullet = series.bullets.push(new am4charts.LabelBullet());

    bullet.label.text = '{valueY}';
    bullet.label.verticalCenter = 'bottom';
    bullet.label.dy = -5;
    bullet.label.fontSize = 14;
    bullet.label.adapter.add('textOutput', (text) => numberWithSpaces(+text));
    bullet.label.adapter.add('fill', () => series.fill);

    let circle = series.bullets.push(new am4charts.CircleBullet());
    circle.circle.fill = am4core.color('#fff');
    circle.circle.strokeWidth = 2;
    circle.circle.radius = 3;
  }
}

function createSeries(chart: XYChart, name: string, data: ChartData[], isTotal: boolean) {
  const series = chart.series.push(new am4charts.LineSeries());
  setupSeries(series, name, data, isTotal);
}

export function setupChart(element: HTMLDivElement, series: Series[], showTotal?: boolean) {
  const chart = am4core.create(element, am4charts.XYChart);

  setupBaseChart(chart);

  chart.colors.list = series.concat(series).map((s) => color(s.color));

  chart.paddingTop = 20;
  chart.paddingLeft = -10;
  chart.paddingBottom = 20;

  let categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
  setupCategoryAxis(categoryAxis);

  let valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
  setupValueAxis(valueAxis);

  chart.data = series[0].values;

  if (showTotal) {
    series.forEach((s) => {
      createSeries(chart, ``, s.total!, true);
    });
  }

  series.forEach((s) => {
    createSeries(chart, `— ${s.title}`, s.values, false);
  });
 
  chart.legend = setupLegend();

  return chart;
}
